import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Calendar, Settings, LogOut, Edit2, Save, X, MapPin, 
  Briefcase, Clock, Mail, Phone, Camera, Lock, CheckCircle,
  CalendarPlus, Download, ExternalLink, Loader2, GraduationCap,
  Heart, Users, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [member, setMember] = useState(null);
  const [events, setEvents] = useState([]);
  const [myRsvps, setMyRsvps] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState({});

  const getToken = () => localStorage.getItem('memberToken');

  const fetchMemberProfile = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/member-login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/members/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('memberInfo');
        navigate('/member-login');
        return;
      }

      const data = await response.json();
      setMember(data);
      setEditData(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/events?upcoming_only=true`);
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, []);

  const fetchMyRsvps = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/members/me/rsvps`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMyRsvps(data.events || []);
    } catch (error) {
      console.error('Error fetching RSVPs:', error);
    }
  }, []);

  useEffect(() => {
    fetchMemberProfile();
    fetchEvents();
    fetchMyRsvps();
  }, [fetchMemberProfile, fetchEvents, fetchMyRsvps]);

  const handleLogout = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('memberInfo');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    const token = getToken();

    try {
      const formData = new FormData();
      
      // Personal Information
      if (editData.full_name !== member.full_name) formData.append('fullName', editData.full_name);
      if (editData.phone !== member.phone) formData.append('phone', editData.phone || '');
      if (editData.date_of_birth !== member.date_of_birth) formData.append('dateOfBirth', editData.date_of_birth || '');
      if (editData.street_address !== member.street_address) formData.append('streetAddress', editData.street_address || '');
      if (editData.city !== member.city) formData.append('city', editData.city || '');
      if (editData.state !== member.state) formData.append('state', editData.state || '');
      if (editData.zip_code !== member.zip_code) formData.append('zipCode', editData.zip_code || '');
      
      // Professional Information
      if (editData.employer !== member.employer) formData.append('employer', editData.employer || '');
      if (editData.job_title !== member.job_title) formData.append('jobTitle', editData.job_title || '');
      if (editData.education_level !== member.education_level) formData.append('educationLevel', editData.education_level || '');
      if (editData.years_in_ece !== member.years_in_ece) formData.append('yearsInECE', editData.years_in_ece || '');
      if (editData.what_brought_you_to_ece !== member.what_brought_you_to_ece) formData.append('whatBroughtYouToECE', editData.what_brought_you_to_ece || '');
      if (editData.current_role !== member.current_role) formData.append('currentRole', editData.current_role || '');
      
      // Skills & Goals
      if (editData.skills_and_talents !== member.skills_and_talents) formData.append('skillsAndTalents', editData.skills_and_talents || '');
      if (editData.willing_to_mentor !== member.willing_to_mentor) formData.append('willingToMentor', editData.willing_to_mentor || '');
      if (editData.what_you_hope_to_gain !== member.what_you_hope_to_gain) formData.append('whatYouHopeToGain', editData.what_you_hope_to_gain || '');
      
      // Personal Details
      if (editData.marital_status !== member.marital_status) formData.append('maritalStatus', editData.marital_status || '');
      if (editData.has_children !== member.has_children) formData.append('hasChildren', editData.has_children || '');
      if (editData.hobbies_and_interests !== member.hobbies_and_interests) formData.append('hobbiesAndInterests', editData.hobbies_and_interests || '');
      
      // Conference & Preferences
      if (editData.support_conference !== member.support_conference) formData.append('supportConference', editData.support_conference || '');
      if (editData.serve_on_committee !== member.serve_on_committee) formData.append('serveOnCommittee', editData.serve_on_committee || '');
      if (editData.photo_consent !== member.photo_consent) formData.append('photoConsent', editData.photo_consent || '');

      const response = await fetch(`${API_URL}/api/members/me`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMember(data.member);
        setEditData(data.member);
        setIsEditing(false);
        toast.success('Profile updated successfully');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRsvp = async (eventId) => {
    const token = getToken();
    setRsvpLoading(prev => ({ ...prev, [eventId]: true }));

    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/rsvp`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Successfully RSVPed to event!');
        fetchEvents();
        fetchMyRsvps();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to RSVP');
      }
    } catch (error) {
      console.error('RSVP error:', error);
      toast.error('Failed to RSVP');
    } finally {
      setRsvpLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const handleCancelRsvp = async (eventId) => {
    const token = getToken();
    setRsvpLoading(prev => ({ ...prev, [eventId]: true }));

    try {
      const response = await fetch(`${API_URL}/api/events/${eventId}/rsvp`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('RSVP cancelled');
        fetchEvents();
        fetchMyRsvps();
      } else {
        toast.error('Failed to cancel RSVP');
      }
    } catch (error) {
      console.error('Cancel RSVP error:', error);
      toast.error('Failed to cancel RSVP');
    } finally {
      setRsvpLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const isRsvped = (eventId) => myRsvps.some(e => e.id === eventId);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand-green mx-auto mb-4" />
          <p className="text-navy-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-brand-navy text-white py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Member Dashboard</h1>
              <p className="text-navy-200">Welcome back, {member?.full_name}</p>
            </div>
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="member-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8">
            {[
              { id: 'profile', label: 'My Profile', icon: User },
              { id: 'events', label: 'Events', icon: Calendar },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-brand-gold text-brand-navy font-medium'
                    : 'border-transparent text-navy-500 hover:text-navy-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Profile Card */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Profile Information</CardTitle>
                {!isEditing ? (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {isEditing ? (
                  <div className="space-y-8">
                    {/* Personal Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-brand-green" />
                        Personal Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input
                            value={editData.full_name || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, full_name: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={editData.phone || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Date of Birth</Label>
                          <Input
                            type="date"
                            value={editData.date_of_birth || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Email (cannot be changed)</Label>
                          <Input value={member?.email || ''} disabled className="mt-1 bg-gray-100" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div className="md:col-span-2">
                          <Label>Street Address</Label>
                          <Input
                            value={editData.street_address || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, street_address: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input
                            value={editData.city || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>State</Label>
                            <Input
                              value={editData.state || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, state: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Zip Code</Label>
                            <Input
                              value={editData.zip_code || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, zip_code: e.target.value }))}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Professional Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-brand-green" />
                        Professional Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Employer</Label>
                          <Input
                            value={editData.employer || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, employer: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Job Title</Label>
                          <Input
                            value={editData.job_title || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, job_title: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Education Level</Label>
                          <select
                            value={editData.education_level || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, education_level: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Select...</option>
                            <option value="High School">High School</option>
                            <option value="Some College">Some College</option>
                            <option value="CDA">CDA</option>
                            <option value="Associate's">Associate's Degree</option>
                            <option value="Bachelor's">Bachelor's Degree</option>
                            <option value="Master's">Master's Degree</option>
                            <option value="Doctorate">Doctorate</option>
                          </select>
                        </div>
                        <div>
                          <Label>Years in ECE</Label>
                          <select
                            value={editData.years_in_ece || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, years_in_ece: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Select...</option>
                            <option value="Less than 1 year">Less than 1 year</option>
                            <option value="1-3 years">1-3 years</option>
                            <option value="3-5 years">3-5 years</option>
                            <option value="5-10 years">5-10 years</option>
                            <option value="10+ years">10+ years</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label>What brought you to ECE?</Label>
                        <Textarea
                          value={editData.what_brought_you_to_ece || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, what_brought_you_to_ece: e.target.value }))}
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                      <div className="mt-4">
                        <Label>Current Role Description</Label>
                        <Textarea
                          value={editData.current_role || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, current_role: e.target.value }))}
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                    </div>

                    {/* Skills & Goals */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-brand-green" />
                        Skills & Goals
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <Label>Skills & Talents</Label>
                          <Textarea
                            value={editData.skills_and_talents || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, skills_and_talents: e.target.value }))}
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label>What You Hope to Gain</Label>
                          <Textarea
                            value={editData.what_you_hope_to_gain || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, what_you_hope_to_gain: e.target.value }))}
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label>Willing to Mentor?</Label>
                          <select
                            value={editData.willing_to_mentor || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, willing_to_mentor: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Select...</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                            <option value="Maybe">Maybe</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Personal Details */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Heart className="w-5 h-5 text-brand-green" />
                        Personal Details
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Marital Status</Label>
                          <select
                            value={editData.marital_status || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, marital_status: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Prefer not to say</option>
                            <option value="Single">Single</option>
                            <option value="Married">Married</option>
                            <option value="Partnered">Partnered</option>
                            <option value="Divorced">Divorced</option>
                            <option value="Widowed">Widowed</option>
                          </select>
                        </div>
                        <div>
                          <Label>Do you have children?</Label>
                          <select
                            value={editData.has_children || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, has_children: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Prefer not to say</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label>Hobbies & Interests</Label>
                        <Textarea
                          value={editData.hobbies_and_interests || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, hobbies_and_interests: e.target.value }))}
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                    </div>

                    {/* Conference & Preferences */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-brand-green" />
                        Conference & Preferences
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Would you support the conference?</Label>
                          <select
                            value={editData.support_conference || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, support_conference: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Select...</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                            <option value="Maybe">Maybe</option>
                          </select>
                        </div>
                        <div>
                          <Label>Photo Consent</Label>
                          <select
                            value={editData.photo_consent || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, photo_consent: e.target.value }))}
                            className="mt-1 w-full rounded-md border border-gray-300 p-2"
                          >
                            <option value="">Select...</option>
                            <option value="Yes">Yes - I consent</option>
                            <option value="No">No - I do not consent</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Label>Serve on Committee (if any)</Label>
                        <Input
                          value={editData.serve_on_committee || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, serve_on_committee: e.target.value }))}
                          className="mt-1"
                          placeholder="e.g., Planning, Marketing, etc."
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* View Mode - Personal Info */}
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-brand-green" />
                        Personal Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <Mail className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Email</p>
                            <p className="font-medium text-navy-900">{member?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Phone className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Phone</p>
                            <p className="font-medium text-navy-900">{member?.phone || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Date of Birth</p>
                            <p className="font-medium text-navy-900">{member?.date_of_birth || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Address</p>
                            <p className="font-medium text-navy-900">
                              {member?.street_address || member?.city || member?.state 
                                ? `${member?.street_address || ''} ${member?.city || ''}, ${member?.state || ''} ${member?.zip_code || ''}`.trim()
                                : 'Not provided'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* View Mode - Professional Info */}
                    <div className="pt-4 border-t">
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-brand-green" />
                        Professional Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <Briefcase className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Position</p>
                            <p className="font-medium text-navy-900">{member?.job_title} at {member?.employer}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <GraduationCap className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Education</p>
                            <p className="font-medium text-navy-900">{member?.education_level || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Experience</p>
                            <p className="font-medium text-navy-900">{member?.years_in_ece || 'Not provided'}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Award className="w-5 h-5 text-navy-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-navy-500">Willing to Mentor</p>
                            <p className="font-medium text-navy-900">{member?.willing_to_mentor || 'Not specified'}</p>
                          </div>
                        </div>
                      </div>
                      {member?.what_brought_you_to_ece && (
                        <div className="mt-4 p-4 bg-navy-50 rounded-lg">
                          <p className="text-sm text-navy-500 mb-1">What brought me to ECE</p>
                          <p className="text-navy-900">{member.what_brought_you_to_ece}</p>
                        </div>
                      )}
                    </div>

                    {/* View Mode - Skills & Goals */}
                    <div className="pt-4 border-t">
                      <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-brand-green" />
                        Skills & Goals
                      </h3>
                      {member?.skills_and_talents && (
                        <div className="mb-4 p-4 bg-gold-50 rounded-lg">
                          <p className="text-sm text-navy-500 mb-1">Skills & Talents</p>
                          <p className="text-navy-900">{member.skills_and_talents}</p>
                        </div>
                      )}
                      {member?.what_you_hope_to_gain && (
                        <div className="p-4 bg-green-50 rounded-lg">
                          <p className="text-sm text-navy-500 mb-1">What I hope to gain</p>
                          <p className="text-navy-900">{member.what_you_hope_to_gain}</p>
                        </div>
                      )}
                    </div>

                    {/* View Mode - Personal Details */}
                    {(member?.hobbies_and_interests || member?.marital_status || member?.has_children) && (
                      <div className="pt-4 border-t">
                        <h3 className="text-lg font-semibold text-navy-900 mb-4 flex items-center gap-2">
                          <Heart className="w-5 h-5 text-brand-green" />
                          Personal Details
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          {member?.marital_status && (
                            <div>
                              <p className="text-sm text-navy-500">Marital Status</p>
                              <p className="font-medium text-navy-900">{member.marital_status}</p>
                            </div>
                          )}
                          {member?.has_children && (
                            <div>
                              <p className="text-sm text-navy-500">Has Children</p>
                              <p className="font-medium text-navy-900">{member.has_children}</p>
                            </div>
                          )}
                        </div>
                        {member?.hobbies_and_interests && (
                          <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                            <p className="text-sm text-navy-500 mb-1">Hobbies & Interests</p>
                            <p className="text-navy-900">{member.hobbies_and_interests}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My RSVPs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Upcoming Events</CardTitle>
              </CardHeader>
              <CardContent>
                {myRsvps.length > 0 ? (
                  <div className="space-y-3">
                    {myRsvps.slice(0, 5).map(event => (
                      <div key={event.id} className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="font-medium text-navy-900">{event.title}</p>
                        <p className="text-sm text-navy-500">{formatDate(event.event_date)}</p>
                        <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          RSVPed
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-navy-500 text-center py-4">No upcoming events</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-navy-900">Upcoming Events</h2>
            {events.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {events.map(event => (
                  <Card key={event.id} className="overflow-hidden">
                    <div className="bg-brand-navy text-white p-4">
                      <h3 className="text-lg font-bold">{event.title}</h3>
                      <p className="text-navy-200">{formatDate(event.event_date)}</p>
                    </div>
                    <CardContent className="p-4 space-y-3">
                      {event.description && (
                        <p className="text-navy-600">{event.description}</p>
                      )}
                      <div className="flex items-center gap-2 text-sm text-navy-500">
                        <Clock className="w-4 h-4" />
                        {event.start_time}{event.end_time && ` - ${event.end_time}`}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2 text-sm text-navy-500">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </div>
                      )}
                      {event.is_virtual && event.virtual_link && (
                        <a 
                          href={event.virtual_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-brand-green hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Join Virtual Meeting
                        </a>
                      )}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <span className="text-sm text-navy-500">
                          {event.rsvp_count || 0} attending
                        </span>
                        <div className="flex gap-2">
                          <a
                            href={`${API_URL}/api/events/${event.id}/ical`}
                            className="inline-flex items-center gap-1 px-3 py-1 text-sm border rounded hover:bg-gray-50"
                          >
                            <Download className="w-4 h-4" />
                            Add to Calendar
                          </a>
                          {isRsvped(event.id) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelRsvp(event.id)}
                              disabled={rsvpLoading[event.id]}
                              data-testid={`cancel-rsvp-${event.id}`}
                            >
                              {rsvpLoading[event.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Cancel RSVP'
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="bg-brand-green hover:bg-green-700"
                              onClick={() => handleRsvp(event.id)}
                              disabled={rsvpLoading[event.id]}
                              data-testid={`rsvp-${event.id}`}
                            >
                              {rsvpLoading[event.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CalendarPlus className="w-4 h-4 mr-1" />
                                  RSVP
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Calendar className="w-12 h-12 text-navy-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-navy-900 mb-2">No Upcoming Events</h3>
                <p className="text-navy-500">Check back soon for new events!</p>
              </Card>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-navy-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Lock className="w-5 h-5 text-navy-600" />
                    <h3 className="font-medium text-navy-900">Change Password</h3>
                  </div>
                  <p className="text-sm text-navy-500 mb-4">
                    Update your password to keep your account secure.
                  </p>
                  <Link to="/member-change-password">
                    <Button variant="outline" data-testid="change-password-btn">
                      Change Password
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
